<template>
  <div class="table-container">
    <div class="table-search">
      <TextInput
        :id="'search-input'"
        :label="''"
        :model-value="searchQuery"
        :placeholder="$t('crm.search_placeholder')"
        @update:model-value="handleSearchInput"
        class="table-view__search-input"
      />
    </div>
    <div class="table-heading">
      <div class="table-heading__title">
        {{ $t('table_view.leads_overview') }}
        <span class="table-heading__count">{{ total }}</span>
      </div>
      <div class="table-heading__actions">
        <Btn icon="plus" @click="handleCreateLead" variant="secondary" class="table-view__add-lead">
          {{ $t('crm.create_lead.button') }}
        </Btn>
      </div>
    </div>
    <EasyDataTable
      :headers="headers"
      :items="formattedLeads"
      :loading="loading"
      :server-items-length="total"
      v-model:server-options="serverOptions"
      buttons-pagination
      @click-row="handleRowClick"
      table-class-name="custom-table"
    >
      <template #item-leadName="{ id, leadName, hasName }">
        <TableRowLink :to="`/crm/lead/${id}`" :aria-label="leadName">
          <span :class="{ 'table-value--no-name': !hasName, 'table-value--has-name': hasName }">
            {{ leadName }}
          </span>
        </TableRowLink>
      </template>

      <template #item-lastUpdated="{ lastUpdated, status }">
        <div class="table-value">
          <div class="status-dot" :class="`status-dot--${status || 'new'}`"></div>
          {{ lastUpdated }}
        </div>
      </template>

      <template #item-actions="item">
        <div class="table-actions">
          <BaseIcon name="eye" class="table-actions__icon" @click="handleEyeClick(item, $event)" />
          <BaseIcon
            name="arrow-up-right"
            class="table-actions__icon"
            @click="handleArrowClick(item, $event)"
          />
        </div>
      </template>
    </EasyDataTable>
  </div>
</template>

<script>
  import { ref, watch, onMounted, nextTick, getCurrentInstance, computed } from 'vue';
  import { useRouter } from 'vue-router';
  import { formatDate } from '@/utils/date';
  import { formatPhone } from '@/utils/phone';
  import { ELEMENTS } from '@/constants/elements';
  import EasyDataTable from 'vue3-easy-data-table';
  import 'vue3-easy-data-table/dist/style.css';

  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';
  import TextInput from '@/components/inputs/TextInput.vue';
  import TableRowLink from '@/components/tables/TableRowLink.vue';

  export default {
    components: {
      EasyDataTable,
      BaseIcon,
      Btn,
      TextInput,
      TableRowLink,
    },

    props: {
      leads: {
        type: Array,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      form: {
        type: Object,
        required: false,
        default: null,
      },
      onCreateLead: {
        type: Function,
        default: null,
      },
      searchQuery: {
        type: String,
        default: '',
      },
      total: {
        type: Number,
        default: 0,
      },
    },

    emits: ['row-click', 'search', 'page-change'],

    setup(props) {
      const instance = getCurrentInstance();
      const router = useRouter();
      const $t = instance.appContext.config.globalProperties.$t;
      const skipPageEmit = ref(false);
      const pageWatchReady = ref(false);
      const serverOptions = ref({
        page: 1,
        rowsPerPage: 50,
      });

      watch(
        serverOptions,
        (options) => {
          if (!pageWatchReady.value) return;
          if (skipPageEmit.value) {
            skipPageEmit.value = false;
            return;
          }
          instance.emit('page-change', {
            page: options.page,
            limit: options.rowsPerPage,
          });
        },
        { deep: true }
      );

      onMounted(() => {
        nextTick(() => {
          pageWatchReady.value = true;
        });
      });

      const headers = [
        { text: $t('table_view.name'), value: 'leadName' },
        { text: $t('table_view.email'), value: 'leadEmail' },
        { text: $t('table_view.phone'), value: 'leadPhone' },
        { text: $t('table_view.address'), value: 'leadAddress' },
      ];

      function formatAddressLine(address) {
        if (!address || typeof address !== 'object') return '';
        const streetLine = [address.street, address.houseNumber || address.house_number]
          .filter((part) => part && String(part).trim())
          .join(' ');
        const cityLine = [address.postalCode || address.postal_code, address.city]
          .filter((part) => part && String(part).trim())
          .join(' ');
        return [streetLine, cityLine].filter(Boolean).join(', ');
      }

      function getFirstTwoNonContentFields(form) {
        if (!form || !form.pages) return [];
        // Flatten all elements from all pages
        const allElements = form.pages.flatMap((page) => page.elements);
        // Find first two non-content fields
        return allElements
          .filter((element) => {
            // Use the ELEMENTS constant to check if it's not in content category
            const elementDefinition = ELEMENTS.find((e) => e.type === element.type);
            return elementDefinition && elementDefinition.category !== 'content';
          })
          .slice(0, 2);
      }

      function getAnswersForFields(submission, formFields, form) {
        return formFields
          .map((field) => {
            const answer = submission.data.find((a) => a.fieldId === field.id);
            if (!answer?.value) return null;

            // For multiple choice, map the IDs to actual choice content
            let displayValue = answer.value;
            if (field.type === 'multiple-choice' && Array.isArray(answer.value)) {
              displayValue = answer.value
                .map((choiceId) => field.props.choices.find((c) => c.id === choiceId)?.content)
                .filter(Boolean)
                .join(', ');
            }

            return {
              fieldId: field.id,
              fieldName: field.props.label,
              value: displayValue,
            };
          })
          .filter(Boolean);
      }

      // Get the first two non-content fields from the form
      const firstTwoFormFields = getFirstTwoNonContentFields(props.form);

      // Add headers for these fields
      firstTwoFormFields.forEach((field) => {
        headers.push({
          text: field.props.label,
          value: `field_${field.id}`,
        });
      });

      // Add "Last updated at" column
      headers.push({ text: $t('table_view.last_updated_at'), value: 'lastUpdated' });

      // Add actions column at the end
      headers.push({ text: '', value: 'actions', width: 100 });

      // Watch for changes in leads prop
      watch(
        () => props.leads,
        (newLeads) => {
          console.log('🔍 TableView leads prop changed:', {
            count: newLeads?.length,
            leads: newLeads,
          });
        },
        { deep: true }
      );

      const formattedLeads = computed(() => {
        console.log('🔍 formattedLeads computed:', {
          propsLeadsCount: props.leads?.length,
          propsLeads: props.leads,
        });
        return props.leads.map((lead) => {
          const formattedDate = formatDate(lead.updated_at || lead.created_at);

          // Create the lead object
          const hasName = Boolean(lead.name || lead.company_name);
          const leadObj = {
            ...lead,
            lastUpdated: formattedDate,
            leadName: lead.name || lead.company_name || $t('crm.no_name_entered'),
            leadEmail: lead.email || '',
            leadPhone: lead.phone ? formatPhone(lead.phone) : '',
            leadAddress: formatAddressLine(lead.address),
            status: lead.status || 'new',
            hasName: hasName,
          };

          // If we have form data and submissions, add dynamic field values
          if (props.form && lead.submissions && lead.submissions.length > 0) {
            const latestSubmission = lead.submissions[0]; // Assuming first is latest
            const fieldAnswers = getAnswersForFields(
              latestSubmission,
              firstTwoFormFields,
              props.form
            );

            fieldAnswers.forEach((field) => {
              leadObj[`field_${field.fieldId}`] = field.value;
            });
          }

          return leadObj;
        });
      });

      const handleRowClick = (lead, event) => {
        // Pass the original event to parent for handling Ctrl/Cmd clicks
        instance.emit('row-click', lead, event);
      };

      const handleEyeClick = (lead, event) => {
        // Stop event propagation to prevent row click
        event.stopPropagation();
        // Same functionality as row click - open drawer
        instance.emit('row-click', lead, event);
      };

      const handleArrowClick = (lead, event) => {
        // Stop event propagation to prevent row click
        event.stopPropagation();
        // Navigate to lead detail page
        router.push(`/crm/lead/${lead.id}`);
      };

      const handleCreateLead = () => {
        if (props.onCreateLead) {
          props.onCreateLead();
        }
      };

      const handleSearchInput = (value) => {
        if (serverOptions.value.page !== 1) {
          skipPageEmit.value = true;
          serverOptions.value.page = 1;
        }
        instance.emit('search', value);
      };

      return {
        headers,
        formattedLeads,
        serverOptions,
        handleRowClick,
        handleEyeClick,
        handleArrowClick,
        handleCreateLead,
        handleSearchInput,
      };
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/scss/base/_mixins.scss';

  // All table styling is now handled by the global _tables.scss
  // Only component-specific styles remain here

  .table-search {
    padding: 0.25rem 1rem;
  }

  .table-view {
    &__add-lead {
      @include lg-down {
        :deep(.btn__content) {
          span {
            display: none;
            visibility: hidden;
          }
          .icon {
            margin-left: 0;
          }
        }
        padding: 0.65rem;
      }
    }

    &__search-input {
      width: 100%;
    }
  }

  .icon {
    color: var(--slate-500);
  }

  .table-actions {
    display: flex;
    gap: 8px;
    align-items: center;
    justify-content: center;

    &__icon {
      width: 16px;
      height: 16px;
      color: var(--slate-500);
      cursor: pointer;
      transition: color 0.2s ease;

      &:hover {
        color: var(--primary-600);
      }
    }
  }

  .table-value--has-name {
    font-weight: 600;
  }

  .table-value--no-name {
    color: var(--slate-300);
    font-style: italic;
  }
</style>
